import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { Box } from '@mui/material'
import { DataGridPro } from '@mui/x-data-grid-pro'
import { KeyboardArrowRight as ChevronRightIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material'

import CustomLocationRow from 'components/locations/CustomLocationRow'
import { setFilters } from 'slices/customer/customerDetailsLocationsSlice'
import { setSortModel } from 'slices/customer/persistentCustomerDetailsLocationsSlice'
import { selectFilters, selectSortModel } from 'data/customer/customerDetailsLocationsSelectors'
import { createDataGridColumns, getDataGridStyling, locationsColumnVisibilityModel, ROW_HEIGHT, VERTICAL } from '../settings'
import LocationExpandView from '../../LocationExpandView'
import LocationsDataGridHeader from './LocationsDataGridHeader'
import NoLocations from './NoLocations'
import { useSearchAndFilter } from '../hooks/useSearchAndFilter'

const LocationsDataGrid = ({
  rows,
  expandedRowIds,
  onSaveTags,
  accountId,
  onDetailPanelExpandedRowIdsChange,
  onRowClick,
  getRowClassName,
  onLocationAnchorElChange,
  onSelectedLocationForMenuChange,
  apiRef,
  customerDetails,
  showAddLocationIcon,
  onLocationStatusChange,
  onTabChange,
}) => {
  const dispatch = useDispatch()
  const filters = useSelector(selectFilters)
  const persistedSortModel = useSelector(selectSortModel)
  const { handleFilterButtonClick, handleResetLocations } = useSearchAndFilter(apiRef)
  const columns = createDataGridColumns({
    onLocationAnchorElChange,
    onSelectedLocationForMenuChange,
    accountId,
  })

  const handleSortModelChange = newSortModel => {
    dispatch(setSortModel(newSortModel))
  }

  const handleFilterModelChange = newFilterModel => {
    dispatch(setFilters({ filterModel: newFilterModel }))
    handleResetLocations(newFilterModel)
  }

  const getDetailPanelContent = useCallback(
    ({ row }) => (
      <Box sx={{ backgroundColor: 'background.default', overflow: 'hidden' }}>
        <LocationExpandView
          caller="datagrid-detail"
          onSaveTags={onSaveTags}
          accountId={accountId}
          selectedLocation={row}
          orientation={VERTICAL}
          dynamicWidth
          showNotes
          showLocationContent={false}
          showSatelliteView
        />
      </Box>
    ),
    [onSaveTags, accountId]
  )

  const getDetailPanelHeight = () => 'auto'

  const getRowId = row => row.id

  const columnVisibilityModel = locationsColumnVisibilityModel()

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      <DataGridPro
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        getRowId={getRowId}
        getRowClassName={getRowClassName}
        onRowClick={onRowClick}
        rowHeight={ROW_HEIGHT}
        columnHeaderHeight={0}
        hideFooter
        disableColumnReorder
        disableColumnSelector
        disableDensitySelector
        disableColumnResize
        checkboxSelection={false}
        filterMode="client"
        sortingMode="client"
        sortModel={persistedSortModel}
        onSortModelChange={handleSortModelChange}
        filterModel={filters.filterModel}
        onFilterModelChange={handleFilterModelChange}
        initialState={{
          filter: {
            filterModel: filters.filterModel,
          },
        }}
        columnVisibilityModel={columnVisibilityModel}
        getDetailPanelContent={getDetailPanelContent}
        getDetailPanelHeight={getDetailPanelHeight}
        detailPanelExpandedRowIds={expandedRowIds}
        onDetailPanelExpandedRowIdsChange={onDetailPanelExpandedRowIdsChange}
        slots={{
          toolbar: LocationsDataGridHeader,
          detailPanelExpandIcon: ChevronRightIcon,
          detailPanelCollapseIcon: ExpandMoreIcon,
          noRowsOverlay: NoLocations,
          row: CustomLocationRow,
        }}
        slotProps={{
          toolbar: {
            showAddLocationIcon,
            onFilterButtonClick: handleFilterButtonClick,
            customerDetails,
            onLocationStatusChange,
            handleResetLocations,
            onTabChange,
          },
        }}
        sx={getDataGridStyling}
      />
    </Box>
  )
}

LocationsDataGrid.propTypes = {
  rows: PropTypes.array.isRequired,
  expandedRowIds: PropTypes.array.isRequired,
  onSaveTags: PropTypes.func,
  accountId: PropTypes.string.isRequired,
  onDetailPanelExpandedRowIdsChange: PropTypes.func.isRequired,
  onRowClick: PropTypes.func.isRequired,
  getRowClassName: PropTypes.func.isRequired,
  onLocationAnchorElChange: PropTypes.func.isRequired,
  onSelectedLocationForMenuChange: PropTypes.func.isRequired,
  apiRef: PropTypes.object.isRequired,
  customerDetails: PropTypes.object.isRequired,
  showAddLocationIcon: PropTypes.bool,
  onLocationStatusChange: PropTypes.func,
  onTabChange: PropTypes.func.isRequired,
}

export default LocationsDataGrid
